import { IMatchDetailDTO, IMatchSideUnit } from "../models/IMatch";

/** Maps display sport type to URL slug used in add-category and category links */
export const sportTypeToSlug = (type: string | undefined): string => {
  if (!type) return "";
  const normalized = type.toLowerCase().trim();
  if (normalized === "beach volleyball") return "beach-volleyball";
  if (normalized === "volleyball") return "volleyball";
  if (normalized === "padel") return "padel";
  if (normalized === "beach tennis") return "beach-tennis";
  if (normalized === "spikeball") return "spikeball";
  if (normalized === "dodgeball") return "dodgeball";
  if (normalized === "beach football") return "beach-football";
  return normalized.replace(/\s+/g, "-");
};

export const getColorForKey = (() => {
  const colorMap: Record<string, string> = {};
  const colors = ["#004AAD", "#FCC353", "#2AAB0A", "#FF4C4C", "#AA00AA", "#00AAAA", "#FF8800"];
  let index = 0;

  return (key: string) => {
    if (!colorMap[key]) {
      colorMap[key] = colors[index % colors.length];
      index++;
    }
    return colorMap[key];
  };
})();

export const getRankName = (rank: number) => {
  return rank === 1
    ? "First"
    : rank === 2
    ? "Second"
    : rank === 3
    ? "Third"
    : rank === 4
    ? "Fourth"
    : rank == 5
    ? "Fifth"
    : rank === 6
    ? "Sixth"
    : rank === 7
    ? "Seventh"
    : rank === 8
    ? "Eighth"
    : rank === 9
    ? "Ninth"
    : rank === 10
    ? "Tenth"
    : "";
};

export const getRoundName = (index: number, numOfTeams: number) => {
  const totalRounds = Math.ceil(Math.log2(numOfTeams));
  const roundFromEnd = totalRounds - index;

  if (roundFromEnd === 1) return "Final";
  if (roundFromEnd === 2) return "Semifinals";
  if (roundFromEnd === 3) return "Quarterfinals";

  const roundSize = Math.pow(2, roundFromEnd);
  return `Round of ${roundSize}`;
};

export const getLetterByIndex = (index: number): string => {
  let result = "";
  index++;

  while (index > 0) {
    index--;
    result = String.fromCharCode(65 + (index % 26)) + result;
    index = Math.floor(index / 26);
  }

  return result;
};

/**
 * One game to a fixed points total, a leaderboard instead of a table/bracket: Americano, Mexicano
 * and Roulette (spec 2026-10-14 §7).
 */
export const isAmericanoFormat = (formatName?: string | null): boolean =>
  formatName === "Americano" || formatName === "Mexicano" || formatName === "Roulette";

/**
 * Run live, one round at a time from the referee portal (no generated schedule): Mexicano and
 * Roulette. Both use the Mexicano setup step and the api/Mexicano routes.
 */
export const isLiveRoundFormat = (formatName?: string | null): boolean =>
  formatName === "Mexicano" || formatName === "Roulette";

const INDIVIDUAL_REGISTRATION_TEAM = "__INDIVIDUAL_REGISTRATION__";

/**
 * A match side's display name: every unit joined, "A", "A & B", "A, B & C", "A, B, C & D".
 * Units 3+ (Roulette) come as extras; a missing list (older servers) means none.
 */
export const formatSideName = (
  name?: string | null,
  name2?: string | null,
  extras?: IMatchSideUnit[] | null
): string => {
  if (!name) return "-";
  const names = [name, name2, ...(extras ?? []).map((u) => u.teamName)]
    .filter((n): n is string => !!n)
    .map((n) => (n === INDIVIDUAL_REGISTRATION_TEAM ? "Individual" : n));
  return names.length === 1 ? names[0] : `${names.slice(0, -1).join(", ")} & ${names[names.length - 1]}`;
};

export const MAX_PLAYERS_PER_SIDE = 12;

/** Roulette rules store units per side; the form shows players per side. Missing = 2 (older servers). */
export const playersPerSideFor = (unitsPerSide?: number | null, unitSize?: number | null): number =>
  (unitsPerSide ?? 2) * (unitSize || 1);

export const unitsPerSideFor = (playersPerSide: number, unitSize: number): number => playersPerSide / unitSize;

/** Roulette side settings (spec 2026-10-14 §4.1): null when acceptable, else the reason. */
export const playersPerSideError = (playersPerSide: number, unitSize: number): string | null => {
  if (!Number.isInteger(unitSize) || unitSize < 1) return "Unit size must be at least 1.";
  if (!Number.isInteger(playersPerSide) || playersPerSide < 1 || playersPerSide > MAX_PLAYERS_PER_SIDE)
    return `Players per side must be between 1 and ${MAX_PLAYERS_PER_SIDE}.`;
  if (playersPerSide % unitSize !== 0) return `Players per side must be a multiple of the unit size (${unitSize}).`;
  return null;
};

/** "Round N of M"; just "Round N" when no rounds are planned (Roulette, open-ended). */
export const formatRoundProgress = (current: number, planned?: number | null): string =>
  planned && planned > 0 ? `Round ${current} of ${planned}` : `Round ${current}`;

/** A stage's planned rounds for a rules summary: 0 = until the organizer finishes (Roulette). */
export const formatPlannedRounds = (roundsCount?: number | null): string =>
  roundsCount == null ? "-" : roundsCount > 0 ? String(roundsCount) : "Until finished";

/**
 * A readable reason from an API failure. The api interceptor rejects with the response body
 * ({ success, message, errors }) and some endpoints answer 200 with success: false in the same
 * shape; a network failure instead reaches callers as a TypeError whose text isn't for users.
 */
export const apiErrorMessage = (error: unknown, fallback: string): string => {
  if (!error || typeof error !== "object" || error instanceof Error) return fallback;
  const { message, errors } = error as { message?: unknown; errors?: unknown };
  if (typeof message === "string" && message.trim()) return message;
  if (Array.isArray(errors) && typeof errors[0] === "string" && errors[0].trim()) return errors[0];
  return fallback;
};

/**
 * Americano/Mexicano/Roulette: the two scores add up to pointsPerMatch, so a score typed for one side gives
 * the other. Null when there's nothing to fill in: another format, no total, not a whole number,
 * or a score above the total (left for validation to flag).
 */
export const otherSideScore = (
  formatName: string | null | undefined,
  pointsPerMatch: number | null | undefined,
  typed: string
): number | null => {
  if (!isAmericanoFormat(formatName) || pointsPerMatch == null || pointsPerMatch <= 0) return null;
  if (!/^\d+$/.test(typed.trim())) return null;
  const value = Number(typed);
  return value <= pointsPerMatch ? pointsPerMatch - value : null;
};

// Matches per round for an Americano/Mexicano stage: floor(players/4), capped by courts.
export const americanoMatchesPerRound = (players: number, courts?: number): number => {
  const full = Math.floor(players / 4);
  return courts && courts >= 1 ? Math.min(full, courts) : full;
};

// Rounds for a full cycle. The no-repeat match budget is floor(players*(players-1)/4)
// (each of the players*(players-1)/2 partnerships used once, 2 partnerships per match).
// Spread over matchesPerRound courts, that needs ceil(budget / matchesPerRound) rounds —
// the last round may be partial (e.g. 11 players on 2 courts: 27 matches => 14 rounds,
// the 14th holding a single match). When players is a multiple of 4 with no court cap this
// is exactly players-1 rounds and everyone partners everyone once; otherwise a couple of
// players end one match short (a perfect cycle is mathematically impossible).
export const americanoFullCycleRounds = (players: number, courts?: number): number => {
  if (players < 2) return 1;
  const m = americanoMatchesPerRound(players, courts);
  if (m < 1) return 1;
  const budget = Math.floor((players * (players - 1)) / 4);
  return Math.max(1, Math.ceil(budget / m));
};

type AmericanoMatchState = Pick<IMatchDetailDTO, "round" | "venue" | "isCompleted" | "gameScores">;

// A match counts as scored once it's completed or has a (possibly live) score on it.
const isMatchScored = (m: AmericanoMatchState): boolean => m.isCompleted || (m.gameScores?.length ?? 0) > 0;

/**
 * Americano "Finish early": the ⋯ menu action shows once at least one match is scored and the stage
 * hasn't ended. Mexicano has its own finish flow (referee portal), so it never qualifies.
 */
export const canFinishAmericanoEarly = (
  formatName: string | null | undefined,
  matches: AmericanoMatchState[],
  ended: boolean
): boolean => formatName === "Americano" && !ended && matches.some(isMatchScored);

/**
 * What finishing early does (mirrors POST Americano/finish): every unscored match is removed, every
 * scored one is kept, and the stage ends after the rounds that have a scored match. Matches with a
 * live score that isn't completed block it - the backend refuses until they're finished or cleared.
 */
export const americanoFinishSummary = (
  matches: AmericanoMatchState[]
): { roundsPlayed: number; unplayedMatches: number; inProgress: string[] } => {
  const scored = matches.filter(isMatchScored);
  return {
    roundsPlayed: new Set(scored.filter((m) => m.round).map((m) => m.round)).size,
    unplayedMatches: matches.length - scored.length,
    inProgress: scored
      .filter((m) => !m.isCompleted)
      .map((m) => [m.round, m.venue].filter(Boolean).join(" · ") || "A match"),
  };
};
